import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from '@/components/ui/table';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  BarChart,
  Bar,
} from 'recharts';
import { Button } from '@/components/ui/button';
import { Loader2, RefreshCw, DollarSign, Users, Activity, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

interface CompanyDrillDownProps {
  companyId: string;
  companyName: string;
  onBack: () => void;
}

interface CompanySummary {
  company_name: string;
  plan_id: string;
  mrr: number;
  total_credits_used: number;
  last_payment_date: string | null;
  subscription_status: string;
  jurisdiction: string | null;
  registration_number: string | null;
}

interface RevenueData {
  month: string;
  mrr: number;
  payment_count: number;
  credits_used: number;
}

interface UsageData {
  feature: string;
  usage_count: number;
  last_used: string;
}

interface UserData {
  id: string;
  user_id: string;
  email: string;
  role: string;
  company_name: string;
  created_at: string;
}

export default function CompanyDrillDown({
  companyId,
  companyName,
  onBack,
}: CompanyDrillDownProps) {
  const [summary, setSummary] = useState<CompanySummary | null>(null);
  const [revenue, setRevenue] = useState<RevenueData[]>([]);
  const [usage, setUsage] = useState<UsageData[]>([]);
  const [users, setUsers] = useState<UserData[]>([]);
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    try {
      // Load company detail
      const { data: detailData, error: detailError } = await supabase.functions.invoke(
        'super-get-company-detail',
        { body: { companyId } }
      );

      if (detailError) throw detailError;
      setSummary(detailData);

      // Load revenue data
      const { data: revenueData, error: revenueError } = await supabase
        .from('v_company_revenue')
        .select('*')
        .eq('company_id', companyId)
        .order('month', { ascending: true });

      if (revenueError) {
        console.error('Revenue error:', revenueError);
      } else {
        setRevenue(
          (revenueData || []).map((r: any) => ({
            month: new Date(r.month).toLocaleDateString('en-US', {
              month: 'short',
              year: 'numeric',
            }),
            mrr: Number(r.mrr),
            payment_count: r.payment_count,
            credits_used: r.credits_used,
          }))
        );
      }

      // Load usage data
      const { data: usageData, error: usageError } = await supabase
        .from('v_company_usage')
        .select('*')
        .eq('company_id', companyId)
        .order('usage_count', { ascending: false });

      if (usageError) {
        console.error('Usage error:', usageError);
      } else {
        setUsage(
          (usageData || []).map((u: any) => ({
            feature: u.feature || 'Unknown',
            usage_count: u.usage_count,
            last_used: new Date(u.last_used).toLocaleDateString(),
          }))
        );
      }

      // Load users
      const { data: userData, error: userError } = await supabase.functions.invoke(
        'super-list-users',
        { body: { companyId } }
      );

      if (userError) {
        console.error('User error:', userError);
      } else {
        setUsers(userData || []);
      }
    } catch (e: any) {
      toast.error(e.message || 'Failed to load company details');
      console.error('Error loading company details:', e);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, [companyId]);

  if (loading) {
    return (
      <div className="p-6 flex justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={onBack} className="gap-2">
          <ArrowLeft className="h-4 w-4" />
          Back to Companies
        </Button>
        <Button variant="outline" size="sm" onClick={load}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Summary Header */}
      <Card className="border-border bg-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="h-5 w-5 text-primary" />
            {summary?.company_name || companyName}
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="space-y-1">
            <div className="text-xs text-muted-foreground">Plan</div>
            <div className="text-lg font-semibold text-foreground">{summary?.plan_id || '—'}</div>
          </div>
          <div className="space-y-1">
            <div className="text-xs text-muted-foreground">MRR</div>
            <div className="text-lg font-semibold text-foreground">
              ${Number(summary?.mrr || 0).toLocaleString()}
            </div>
          </div>
          <div className="space-y-1">
            <div className="text-xs text-muted-foreground">Credits Used (90d)</div>
            <div className="text-lg font-semibold text-foreground">
              {summary?.total_credits_used || 0}
            </div>
          </div>
          <div className="space-y-1">
            <div className="text-xs text-muted-foreground">Last Payment</div>
            <div className="text-lg font-semibold text-foreground">
              {summary?.last_payment_date || '—'}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Revenue Trend */}
      {revenue.length > 0 && (
        <Card className="border-border bg-card">
          <CardHeader>
            <CardTitle>Revenue Trend (Last 12 Months)</CardTitle>
          </CardHeader>
          <CardContent className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={revenue}>
                <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" />
                <YAxis stroke="hsl(var(--muted-foreground))" />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'hsl(var(--card))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '0.5rem',
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="mrr"
                  stroke="hsl(var(--primary))"
                  name="MRR (USD)"
                  strokeWidth={2}
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Usage Chart */}
      {usage.length > 0 && (
        <Card className="border-border bg-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5 text-primary" />
              Feature Usage (Last 90 Days)
            </CardTitle>
          </CardHeader>
          <CardContent className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={usage}>
                <XAxis dataKey="feature" stroke="hsl(var(--muted-foreground))" />
                <YAxis stroke="hsl(var(--muted-foreground))" />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'hsl(var(--card))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '0.5rem',
                  }}
                />
                <Bar dataKey="usage_count" fill="hsl(var(--primary))" name="Usage Count" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Team Snapshot */}
      <Card className="border-border bg-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5 text-primary" />
            Team Members ({users.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Joined</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u.id}>
                    <TableCell className="font-medium">{u.email}</TableCell>
                    <TableCell>
                      <span className="capitalize">{u.role.replace('_', ' ')}</span>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {new Date(u.created_at).toLocaleDateString()}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
